import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, CheckCircle, DollarSign, Clock, MapPin, ArrowRight } from 'lucide-react';
import { services, contactInfo } from '../data/mock';

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Tous les services' },
    { id: 'wound-care', label: 'Soins de plaies' },
    { id: 'foot-care', label: 'Soins de pieds' },
    { id: 'specialized', label: 'Soins spécialisés' }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <CheckCircle size={16} className="text-green-500" />
            <span>Services professionnels à domicile</span>
          </div>
          
          <h1 className="heading-hero mb-4">
            Nos services de soins
          </h1>
          
          <p className="body-large text-gray-700 max-w-2xl mx-auto">
            Des soins spécialisés adaptés à vos besoins, dans le confort de votre foyer. 
            Qualité professionnelle et approche personnalisée.
          </p>
        </div>
      </section>

      {/* Service Categories Filter */}
      <section className="py-8 bg-white sticky top-20 z-50 border-b border-gray-200">
        <div className="container">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`btn-tag hover-scale ${
                  selectedCategory === category.id ? 'active' : ''
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            {filteredServices.map((service) => (
              <div key={service.id} className={`service-card ${service.accent} hover-lift`}>
                {/* Service Image */}
                <div className="mb-6">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>

                {/* Service Header */}
                <div className="mb-4">
                  <h3 className="heading-2 mb-2">{service.title}</h3>
                  <p className="body-medium text-gray-700">
                    {service.description}
                  </p>
                </div>

                {/* Service Details */}
                <div className="space-y-4 mb-6">
                  <h4 className="heading-3 text-gray-800">Services inclus :</h4>
                  <ul className="space-y-2">
                    {service.services.map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle size={16} className="text-green-500 mt-1 flex-shrink-0" />
                        <span className="body-small text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pricing */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <DollarSign size={18} className="text-green-600" />
                      <span className="body-medium font-semibold text-green-600">
                        {service.pricing}
                      </span>
                    </div>
                  </div>
                  
                  <Link 
                    to="/contact" 
                    className="btn-primary w-full justify-center hover-scale"
                  >
                    <Calendar size={16} className="mr-2" />
                    <span className="button-text">Réserver ce service</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas & Info */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Service Information */}
            <div className="space-y-6">
              <h2 className="heading-1">Informations pratiques</h2>
              
              <div className="space-y-4">
                <div className="service-card accent-blue">
                  <div className="flex items-start space-x-3">
                    <Clock size={24} className="text-blue-600 mt-1" />
                    <div>
                      <h3 className="heading-3 mb-2">Horaires de service</h3>
                      <div className="space-y-1">
                        <p className="body-small">{contactInfo.hours.weekdays}</p>
                        <p className="body-small">{contactInfo.hours.friday}</p>
                        <p className="body-small text-gray-600 font-medium">
                          Rendez-vous planifiés seulement
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-card accent-green">
                  <div className="flex items-start space-x-3">
                    <MapPin size={24} className="text-green-600 mt-1" />
                    <div>
                      <h3 className="heading-3 mb-2">Zone de service</h3>
                      <p className="body-small text-gray-700">
                        {contactInfo.serviceArea}
                      </p>
                      <p className="caption text-gray-600 mt-1">
                        Frais de déplacement peuvent s'appliquer selon la distance
                      </p>
                    </div>
                  </div>
                </div>

                <div className="service-card accent-cream">
                  <div className="flex items-start space-x-3">
                    <CheckCircle size={24} className="text-purple-600 mt-1" />
                    <div>
                      <h3 className="heading-3 mb-2">Ce qui est inclus</h3>
                      <ul className="space-y-1">
                        <li className="body-small">• Évaluation complète</li>
                        <li className="body-small">• Matériel médical fourni</li>
                        <li className="body-small">• Rapport détaillé</li>
                        <li className="body-small">• Suivi personnalisé</li>
                        <li className="body-small">• Conseils préventifs</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="space-y-6">
              <h2 className="heading-1">Prêt à commencer ?</h2>
              
              <div className="service-card accent-grey">
                <h3 className="heading-2 mb-4">Contactez-nous maintenant</h3>
                
                <div className="space-y-4">
                  <p className="body-medium text-gray-700">
                    Prenez rendez-vous dès aujourd'hui pour une consultation ou 
                    appelez-nous pour toute question urgente.
                  </p>
                  
                  <div className="grid grid-cols-1 gap-3">
                    <Link to="/contact" className="btn-primary justify-center hover-scale">
                      <Calendar size={16} className="mr-2" />
                      <span className="button-text">Prendre rendez-vous</span>
                    </Link>
                    
                    <a 
                      href="tel:819-469-4419" 
                      className="btn-secondary justify-center hover-scale"
                    >
                      <Phone size={16} className="mr-2" />
                      <span className="button-text">Appeler maintenant</span>
                    </a>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <p className="body-small text-gray-600 text-center">
                      Soins planifiés - Urgences : appelez le 911
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ Quick Links */}
              <div className="service-card accent-blue">
                <h3 className="heading-3 mb-4">Questions fréquentes</h3>
                <div className="space-y-3">
                  <div className="border-b border-gray-200 pb-2">
                    <p className="body-small font-medium">Comment se déroule la première visite ?</p>
                    <p className="caption text-gray-600">
                      Évaluation complète, plan de soins personnalisé et premier traitement si nécessaire.
                    </p>
                  </div>
                  
                  
                  <div className="pb-2">
                    <p className="body-small font-medium">Durée typique d'une visite ?</p>
                    <p className="caption text-gray-600">
                      Entre 30-60 minutes selon le type de soins requis.
                    </p>
                  </div>
                </div>
                
                <Link to="/contact" className="btn-secondary w-full justify-center mt-4 hover-scale">
                  <span className="button-text">Plus de questions ?</span>
                  <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;